import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";

import { Select2FAFormData, select2FASchema } from "../types/select-2fa.schema";

type Select2FAFormProps = {
  onSubmit: (data: Select2FAFormData["type"]) => Promise<void>;
};

export function Select2FAForm({ onSubmit: handleSubmit }: Select2FAFormProps) {
  const form = useForm<Select2FAFormData>({
    resolver: zodResolver(select2FASchema),
  });

  async function onSubmit(data: Select2FAFormData) {
    await handleSubmit(data.type);
  }

  const isEmailPreferred = form.watch("type") === "email";

  return (
    <Card>
      <Card.Header
        title="Setup 2FA"
        description="Select your preferred method"
      />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card.Content>
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      <FormItem className="relative flex w-full items-center">
                        <div className="flex-1">
                          <FormControl className="absolute left-5 top-5 mt-0.5">
                            <RadioGroupItem value="totp" />
                          </FormControl>
                          <FormLabel
                            className={cn(
                              "flex w-full flex-1 cursor-pointer flex-col rounded-xl p-5 ring-1 ring-border",
                              field.value === "totp" &&
                                "bg-primary-foreground ring-2 ring-primary dark:bg-card dark:ring-border",
                            )}
                          >
                            <span className="ml-6 text-sm text-secondary">
                              Authenticator App{" "}
                              <Badge variant="outline">Recommended</Badge>
                            </span>
                            <FormDescription className="ml-6 text-sm font-normal text-muted">
                              A service such as Google Authenticator
                            </FormDescription>
                          </FormLabel>
                        </div>
                      </FormItem>

                      <FormItem className="relative flex w-full items-center">
                        <div className="flex-1">
                          <FormControl className="absolute left-5 top-5 mt-0.5">
                            <RadioGroupItem value="email" />
                          </FormControl>
                          <FormLabel
                            className={cn(
                              "flex w-full flex-1 cursor-pointer flex-col rounded-xl p-5 ring-1 ring-border",
                              field.value === "email" &&
                                "bg-primary-foreground ring-2 ring-primary dark:bg-card dark:ring-border",
                            )}
                          >
                            <span className="ml-6 text-sm text-secondary">
                              Email
                            </span>
                            <FormDescription className="ml-6 text-sm font-normal text-muted">
                              A one-time passcode sent via email
                            </FormDescription>
                          </FormLabel>
                        </div>
                      </FormItem>

                      <FormItem className="relative flex items-center">
                        <div className="flex-1">
                          <FormControl className="absolute left-5 top-5 mt-0.5">
                            <RadioGroupItem value="sms" />
                          </FormControl>
                          <FormLabel
                            className={cn(
                              "flex flex-1 cursor-pointer flex-col rounded-xl p-5 ring-1 ring-border",
                              field.value === "sms" &&
                                "bg-primary-foreground ring-2 ring-primary dark:bg-card dark:ring-border",
                            )}
                          >
                            <span className="ml-6 text-sm text-secondary">
                              SMS
                            </span>
                            <FormDescription className="ml-6 text-sm font-normal text-muted">
                              A one-time passcode sent via SMS
                            </FormDescription>
                          </FormLabel>
                        </div>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card.Content>
          <Card.Footer className="flex items-center justify-between">
            <p className="text-sm text-secondary">
              Step 1 of {isEmailPreferred ? 1 : 2}
            </p>
            <Button
              type="submit"
              disabled={!form.formState.isValid || form.formState.isSubmitting}
            >
              {isEmailPreferred ? "Verify" : "Continue"}
            </Button>
          </Card.Footer>
        </form>
      </Form>
    </Card>
  );
}
